import React from "react";
import Input from "components/ui/Input";
import Icon from "components/AppIcon";

const COUNTRY_OPTIONS = [
  "United Kingdom", "United States", "Germany", "France", "Singapore",
  "United Arab Emirates", "Canada", "Australia", "Japan", "Switzerland"
];

const ENTITY_TYPES = [
  { value: "limited_company", label: "Limited Company" },
  { value: "llp", label: "Limited Liability Partnership" },
  { value: "trust", label: "Trust" },
  { value: "foundation", label: "Foundation" },
  { value: "fund", label: "Investment Fund" },
  { value: "sovereign", label: "Sovereign Entity" },
];

export default function StepOrganisationDetails({ data, onChange, errors }) {
  const handleChange = (field) => (e) => {
    onChange({ ...data, [field]: e?.target?.value });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 p-4 rounded-lg bg-[rgba(59,76,76,0.07)] border border-blue-100">
        <Icon name="Info" size={18} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
        <p className="text-sm font-caption text-primary">
          Please provide your organisation's legal details exactly as they appear on official registration documents. All information is encrypted and stored securely.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
        <div className="md:col-span-2">
          <Input
            label="Legal Entity Name"
            type="text"
            placeholder="e.g. Meridian Capital Partners LLP"
            value={data?.legalName || ""}
            onChange={handleChange("legalName")}
            required
            error={errors?.legalName}
            description="Full registered legal name of your organisation"
          />
        </div>

        <div>
          <label className="block text-sm font-caption font-medium text-foreground mb-1">
            Entity Type <span className="text-error">*</span>
          </label>
          <select
            value={data?.entityType || ""}
            onChange={handleChange("entityType")}
            className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground font-caption text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">Select entity type</option>
            {ENTITY_TYPES?.map((t) => (
              <option key={t?.value} value={t?.value}>{t?.label}</option>
            ))}
          </select>
          {errors?.entityType && <p className="text-error text-xs mt-1">{errors?.entityType}</p>}
        </div>

        <Input
          label="Company Registration Number"
          type="text"
          placeholder="e.g. 12345678"
          value={data?.registrationNumber || ""}
          onChange={handleChange("registrationNumber")}
          required
          error={errors?.registrationNumber}
        />

        <Input
          label="Registered Address Line 1"
          type="text"
          placeholder="e.g. 10 Canary Wharf"
          value={data?.addressLine1 || ""}
          onChange={handleChange("addressLine1")}
          required
          error={errors?.addressLine1}
        />

        <Input
          label="Address Line 2"
          type="text"
          placeholder="e.g. Suite 400"
          value={data?.addressLine2 || ""}
          onChange={handleChange("addressLine2")}
        />

        <Input
          label="City"
          type="text"
          placeholder="e.g. London"
          value={data?.city || ""}
          onChange={handleChange("city")}
          required
          error={errors?.city}
        />

        <Input
          label="Postcode / ZIP"
          type="text"
          placeholder="e.g. E14 5AB"
          value={data?.postcode || ""}
          onChange={handleChange("postcode")}
          required
          error={errors?.postcode}
        />

        <div>
          <label className="block text-sm font-caption font-medium text-foreground mb-1">
            Country of Incorporation <span className="text-error">*</span>
          </label>
          <select
            value={data?.country || ""}
            onChange={handleChange("country")}
            className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground font-caption text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">Select country</option>
            {COUNTRY_OPTIONS?.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          {errors?.country && <p className="text-error text-xs mt-1">{errors?.country}</p>}
        </div>

        <Input
          label="LEI (Legal Entity Identifier)"
          type="text"
          placeholder="e.g. 213800WSGIIZCXF1P572"
          value={data?.lei || ""}
          onChange={handleChange("lei")}
          description="20-character alphanumeric code (optional but recommended)"
        />

        <div className="md:col-span-2">
          <Input
            label="Primary Contact Name"
            type="text"
            placeholder="e.g. James Donovan"
            value={data?.contactName || ""}
            onChange={handleChange("contactName")}
            required
            error={errors?.contactName}
          />
        </div>

        <Input
          label="Contact Email"
          type="email"
          placeholder="e.g. j.donovan@meridian.com"
          value={data?.contactEmail || ""}
          onChange={handleChange("contactEmail")}
          required
          error={errors?.contactEmail}
        />

        <Input
          label="Contact Phone"
          type="tel"
          placeholder="e.g. +44 20 7946 0958"
          value={data?.contactPhone || ""}
          onChange={handleChange("contactPhone")}
          required
          error={errors?.contactPhone}
        />
      </div>
    </div>
  );
}